import PauseSvg from '@/assets/PauseSvg.svg';
import PlaySvg from '@/assets/PlaySvg.svg';
import SkipToEndSvg from '@/assets/SkipToEndSvg.svg';
import SkipToStartSvg from '@/assets/SkipToStartSvg.svg';
import VolumeMuteSvg from '@/assets/VolumeMuteSvg.svg';
import VolumeStatusSvg from '@/assets/VolumeStatusSvg.svg';
import { useAudioSettings } from '@/utils/providers/audioSettingsContext/useAudioSettings';
import { FC, useCallback, useEffect, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { useAudioPlayer } from 'react-use-audio-player';

interface Props {
  audioUrl?: string;
  controls?: boolean;
  onEnd?: () => void;
}

const AudioPlayer: FC<Props> = ({ audioUrl, controls, onEnd }) => {
  const { isNarratorEnabled } = useAudioSettings();
  const { cleanup, isMuted, isPlaying, load, pause, play, stop, toggleMute, togglePlayPause } =
    useAudioPlayer();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;

  const handlePlayPause = useCallback(() => togglePlayPause(), [togglePlayPause]);
  const handleMuteUnmute = useCallback(() => toggleMute(), [toggleMute]);

  const skipToStart = () => {
    stop();
    play();
  };
  const skipToEnd = () => {
    onEnd?.();
    stop();
  };

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (audioUrl && isNarratorEnabled) {
      load(audioUrl, {
        autoplay: true,
        html5: true,
        onend: () => onEndRef.current?.(),
      });
    }
    return () => {
      pause();
      stop();
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl, isNarratorEnabled]);

  if (!controls) {
    return null;
  }
  return (
    <div className="bg-gray-100 px-4 py-[15px]">
      <div className="flex items-center justify-center gap-m">
        <Button className="rounded-circle p-2" onClick={skipToStart}>
          <img alt="skip-to-start" src={SkipToStartSvg} />
        </Button>
        <Button className="rounded-circle p-2" onClick={handlePlayPause}>
          <img alt="play-pause" src={isPlaying ? PauseSvg : PlaySvg} />
        </Button>
        <Button className="rounded-circle p-2" onClick={skipToEnd}>
          <img alt="skip-to-end" src={SkipToEndSvg} />
        </Button>
        <Button className="rounded-circle p-2" onClick={handleMuteUnmute}>
          <img alt="mute" src={isMuted ? VolumeMuteSvg : VolumeStatusSvg} />
        </Button>
      </div>
    </div>
  );
};

export default AudioPlayer;
